// Import necessary React hooks and components
import React, { useState, useEffect } from 'react';
// Icons for various UI elements
import { 
  XMarkIcon, 
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CubeIcon,
  HashtagIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
// Type definitions for TagMatch and Product data structures
import { TagMatch, Product } from '../types/index';
// Service to fetch product data from the backend
import { productService } from '../services/productService';
// Unified API hook for accessing both Smart and SWS APIs
import { useUnifiedApi } from '../hooks/useUnifiedApi';
// Reusable loading spinner component
import LoadingSpinner from './LoadingSpinner';
// QR Scanner components
import SingleTagQRScannerModal from './SingleTagQRScannerModal';
import QRScannerButton from './QRScannerButton';
// Authentication and permissions hook
import { useAuth, usePermissions } from '../contexts/AuthContext';

/**
 * EditTagModalProps - Configuration for the tag editing popup
 * This modal allows users to perform various operations on existing tag matches
 */
interface EditTagModalProps {
  isOpen: boolean;              // Whether the modal should be visible
  onClose: () => void;          // Function to call when closing the modal
  onUpdateQuantity: (tagMatch: TagMatch, newQuantity: number) => void; // Update quantity
  onUpdateProduct: (tagMatch: TagMatch, newProductId: string, newQuantity: number) => void; // Update product
  onClearTag: (tagMatch: TagMatch) => void;  // Clear tag data (make available for reuse)
  onReplaceTag: (tagMatch: TagMatch, newTagNumber: string) => void; // Replace with new tag
  tagMatch: TagMatch | null;    // The tag match being edited (null if none)
  currentProduct: Product | null; // The product currently matched to this tag
  isLoading?: boolean;          // Whether a save/delete operation is in progress
}

// Type for the different actions a user can take
type ActionType = 'update_quantity' | 'change_product' | 'clear_tag' | 'replace_tag' | null;

/**
 * EditTagModal Component
 * Action-based modal that presents users with clear choices for tag management
 */
const EditTagModal: React.FC<EditTagModalProps> = ({
  isOpen,
  onClose,
  onUpdateQuantity,
  onUpdateProduct,
  onClearTag,
  onReplaceTag,
  tagMatch,
  currentProduct,
  isLoading = false
}) => {
  // Get unified API instance
  const api = useUnifiedApi();
  // State for action selection flow
  const [selectedAction, setSelectedAction] = useState<ActionType>(null);
  
  // State for update quantity action
  const [quantityInput, setQuantityInput] = useState<string>('1');
  
  // State for change product action
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // State for replace tag action
  const [newTagNumber, setNewTagNumber] = useState('');
  const [isCheckingTag, setIsCheckingTag] = useState(false);
  const [tagCheckResult, setTagCheckResult] = useState<{available: boolean; reason?: string; matchedTo?: string} | null>(null);
  
  // State for confirmations
  const [showQuantityConfirm, setShowQuantityConfirm] = useState(false);
  const [showProductConfirm, setShowProductConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  
  // State for quantity validation
  const [quantityError, setQuantityError] = useState<string>('');
  
  // QR Scanner state
  const [showQRScanner, setShowQRScanner] = useState(false);

  /**
   * Reset modal state when it opens or closes
   */
  useEffect(() => {
    if (isOpen) {
      // Reset to action selection when modal opens
      setSelectedAction(null);
      setShowQuantityConfirm(false);
      setShowProductConfirm(false);
      setShowClearConfirm(false);
      setShowReplaceConfirm(false);
      setNewTagNumber('');
      setSearchTerm('');
      setHasSearched(false);
      setTagCheckResult(null);
      setQuantityError('');
      
      // Set current values
      if (tagMatch) {
        setQuantityInput(tagMatch.quantity.toString());
        setSelectedProductId(tagMatch.productId);
      }
    }
  }, [isOpen, tagMatch]);

  /**
   * Reset search state when changing to product action
   */
  useEffect(() => {
    if (selectedAction === 'change_product') {
      setSearchTerm('');
      setProducts([]);
      setHasSearched(false);
    }
  }, [selectedAction]);

  /**
   * Search products from the backend
   */
  const searchProducts = async () => {
    if (!searchTerm.trim()) return;
    
    setLoadingProducts(true);
    setHasSearched(true);
    try {
      // Search for products matching the search term
      const results = await productService.searchProducts(searchTerm);
      setProducts(results);
    } catch (error) {
      console.error('Failed to search products:', error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  /**
   * Check if a tag is available for use
   */
  const checkTagAvailability = async (tagNumber: string) => {
    if (tagNumber.length < 12) {
      setTagCheckResult(null);
      return;
    }

    if (!api) {
      setTagCheckResult({
        available: false,
        reason: 'API service not available'
      });
      return;
    }

    setIsCheckingTag(true);
    try {
      // Use unified API to scan the tag
      const tagInfo = await api.scanTag(tagNumber);
      
      if (tagInfo) {
        // Check if this is the same tag we're replacing (should be allowed)
        if (tagMatch && tagNumber.toUpperCase() === tagMatch.tagNumber.toUpperCase()) {
          setTagCheckResult({
            available: true,
            reason: 'This is the current tag (no change will be made)'
          });
        } else {
          // Tag exists and is matched to a different product - not available
          setTagCheckResult({
            available: false,
            reason: 'This tag is already in use',
            matchedTo: tagInfo.productName || tagInfo.productSku || 'Unknown product'
          });
        }
      } else {
        // Tag not found - available for use
        setTagCheckResult({
          available: true
        });
      }
    } catch (error) {
      console.error('Failed to check tag availability:', error);
      setTagCheckResult({
        available: false,
        reason: 'Failed to validate tag - please try again'
      });
    } finally {
      setIsCheckingTag(false);
    }
  };

  /**
   * Validate quantity input
   */
  const validateQuantity = (value: string): string => {
    // Check if empty
    if (!value.trim()) {
      return 'Quantity is required';
    }
    
    // Check if it's a valid number
    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'Please enter a valid number';
    }
    
    // Check if it's a positive integer
    if (!Number.isInteger(num) || num <= 0) {
      return 'Please enter a positive integer (whole number greater than 0)';
    }
    
    return '';
  };

  /**
   * Handle quantity input change with validation
   */
  const handleQuantityChange = (value: string) => {
    setQuantityInput(value);
    const error = validateQuantity(value);
    setQuantityError(error);
  };

  const incrementQuantity = () => {
    const current = parseInt(quantityInput) || 1;
    const newValue = (current + 1).toString();
    handleQuantityChange(newValue);
  };

  const decrementQuantity = () => {
    const current = parseInt(quantityInput) || 1;
    if (current > 1) {
      const newValue = (current - 1).toString();
      handleQuantityChange(newValue);
    }
  };

  /**
   * Handle action selection
   */
  const handleActionSelect = (action: ActionType) => {
    setSelectedAction(action);
    
    // For clear and replace actions, go straight to confirmation
    if (action === 'clear_tag') {
      setShowClearConfirm(true);
    } else if (action === 'replace_tag') {
      // Don't show confirm yet, need to get new tag number first
    }
  };

  /**
   * Handle quantity update submission
   */
  const handleQuantityUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate quantity before submitting
    const error = validateQuantity(quantityInput);
    if (error) {
      setQuantityError(error);
      return;
    }
    
    const parsedQuantity = parseInt(quantityInput);
    if (tagMatch && parsedQuantity > 0) {
      setShowQuantityConfirm(true);
    }
  };

  /**
   * Confirm quantity update
   */
  const confirmQuantityUpdate = () => {
    const parsedQuantity = parseInt(quantityInput);
    if (tagMatch && parsedQuantity > 0) {
      onUpdateQuantity(tagMatch, parsedQuantity);
    }
  };

  /**
   * Handle product change submission
   */
  const handleProductChange = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (tagMatch && selectedProductId) {
      setShowProductConfirm(true);
    }
  };

  /**
   * Confirm product change
   */
  const confirmProductChange = () => {
    if (tagMatch && selectedProductId) {
      // Keep the current quantity when changing product
      onUpdateProduct(tagMatch, selectedProductId, tagMatch.quantity);
    }
  };

  /**
   * Handle clear tag confirmation
   */
  const handleClearTag = () => {
    if (tagMatch) {
      onClearTag(tagMatch);
    }
  };

  /**
   * Handle replace tag submission
   */
  const handleReplaceTag = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTagNumber.trim().length >= 12 && tagCheckResult?.available) {
      setShowReplaceConfirm(true);
    }
  };

  /**
   * Handle new tag number change
   */
  const handleNewTagNumberChange = (value: string) => {
    const formatted = formatTagNumber(value);
    setNewTagNumber(formatted);
    
    // Check availability when tag is complete
    if (formatted.length >= 12) {
      checkTagAvailability(formatted);
    } else {
      setTagCheckResult(null);
    }
  };

  /**
   * Handle replace tag confirmation
   */
  const confirmReplaceTag = () => {
    if (tagMatch && newTagNumber) {
      onReplaceTag(tagMatch, newTagNumber.toUpperCase());
    }
  };
  
  /**
   * Handle QR scan confirmation for replace tag
   */
  const handleQRScanConfirmed = (tag: string) => {
    handleNewTagNumberChange(tag);
    setShowQRScanner(false);
  };

  /**
   * Format tag number for display
   */
  const formatTagNumber = (tag: string) => {
    return tag.toUpperCase().replace(/[^A-Z0-9]/g, '');
  };

  // Get permissions from context
  const permissions = usePermissions();
  const { user } = useAuth();
  
  // Combine permissions with business rules
  // Check granular permissions for both Smart and SWS
  const hasPermissionUpdateQuantity = permissions.canUpdateQuantity ?? false;
  const hasPermissionChangeProduct = permissions.canChangeProduct ?? false;
  const hasPermissionClearTag = permissions.canClearTagData ?? false;
  const hasPermissionReplaceTag = permissions.canReplaceTag ?? false;
  
  // Business rules combined with permissions
  const canUpdateQuantity = hasPermissionUpdateQuantity && 
    tagMatch && (tagMatch.status === 'pending' || tagMatch.status === 'on_hand');
  const canChangeProduct = hasPermissionChangeProduct && 
    tagMatch && (tagMatch.status === 'pending' || tagMatch.status === 'on_hand');
  const canClearTag = hasPermissionClearTag && 
    tagMatch && tagMatch.status === 'pending';
  const canReplaceTag = hasPermissionReplaceTag && 
    tagMatch && (tagMatch.status === 'pending' || tagMatch.status === 'on_hand');
  
  /**
   * SWS CHANGE PRODUCT FEATURE STATUS:
   * - Smart API: Always works (both production and testing)
   * - SWS API Production: Works correctly
   * - SWS API Testing/Staging: Returns success but doesn't actually update
   * 
   * Show Change Product option for:
   * 1. Smart API users (always)
   * 2. SWS API users ONLY when connected to production
   */
  const isSwsProduction = api?.getCurrentSystem() === 'sws' && api?.isProductionEnvironment?.();
  const showChangeProductOption = (api?.getCurrentSystem() === 'smart' || isSwsProduction) && hasPermissionChangeProduct;

  if (!isOpen || !tagMatch) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-secondary-400 hover:text-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={onClose}
              disabled={isLoading}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="w-full">
              <h3 className="text-lg leading-6 font-medium text-secondary-900 mb-4">
                Manage Tag
              </h3>
              
              {/* Current Tag Information - Always Visible */}
              <div className="bg-secondary-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-secondary-700">Tag Number:</p>
                    <p className="text-secondary-900 font-mono">{tagMatch.tagNumber}</p>
                  </div>
                  <div>
                    <p className="font-medium text-secondary-700">Status:</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      tagMatch.status === 'pending' 
                        ? 'bg-yellow-100 text-yellow-800'
                        : tagMatch.status === 'on_hand'
                        ? 'bg-green-100 text-green-800'
                        : tagMatch.status === 'not_matched'
                        ? 'bg-blue-100 text-blue-800'
                        : tagMatch.status === 'abandon'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {tagMatch.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-secondary-700">Current Product:</p>
                    <p className="text-secondary-900">{currentProduct?.itemNumber || 'Unknown'}</p>
                    <p className="text-secondary-600 text-xs">{currentProduct?.description || 'Product not found'}</p>
                  </div>
                  <div>
                    <p className="font-medium text-secondary-700">Package Quantity:</p>
                    <p className="text-secondary-900">{tagMatch.quantity}</p>
                  </div>
                  {tagMatch.location && (
                    <div>
                      <p className="font-medium text-secondary-700">Location:</p>
                      <p className="text-secondary-900">{tagMatch.location}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Selection - Show when no action is selected */}
              {!selectedAction && !showClearConfirm && !showReplaceConfirm && (
                <div>
                  {/* Check if user has any permissions */}
                  {(hasPermissionUpdateQuantity || hasPermissionChangeProduct || hasPermissionClearTag || hasPermissionReplaceTag) ? (
                    <>
                      <p className="text-sm text-secondary-700 mb-4">
                        What would you like to do with this tag?
                      </p>
                      
                      <div className="space-y-3">
                    {/* Update Quantity - Only show if user has permission */}
                    {hasPermissionUpdateQuantity && (
                      <button
                        onClick={() => handleActionSelect('update_quantity')}
                        disabled={!canUpdateQuantity}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                          canUpdateQuantity
                            ? 'border-secondary-200 hover:border-primary-300 hover:bg-primary-50'
                            : 'border-secondary-100 bg-secondary-50 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-start">
                          <HashtagIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3" />
                          <div className="flex-1">
                            <p className="font-medium text-secondary-900">Update Package Quantity</p>
                            <p className="text-sm text-secondary-600 mt-1">
                              Change the quantity of items associated to this tag (current: {tagMatch.quantity})
                            </p>
                            {!canUpdateQuantity && tagMatch.status !== 'pending' && tagMatch.status !== 'on_hand' && (
                              <p className="text-xs text-warning-600 mt-1">
                                Only available for pending or on-hand tags
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Change Product - Only shown for Smart API users */}
                    {showChangeProductOption && (
                      <button
                        onClick={() => handleActionSelect('change_product')}
                        disabled={!canChangeProduct}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                          canChangeProduct
                            ? 'border-secondary-200 hover:border-primary-300 hover:bg-primary-50'
                            : 'border-secondary-100 bg-secondary-50 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-start">
                          <CubeIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3" />
                          <div className="flex-1">
                            <p className="font-medium text-secondary-900">Change Product Association</p>
                            <p className="text-sm text-secondary-600 mt-1">
                              Assign this tag to a different product (current: {currentProduct?.itemNumber || 'Unknown'})
                            </p>
                          </div>
                        </div>
                      </button>
                    )}
                    
                    {/* Message for SWS users about change product not available */}
                    {!showChangeProductOption && hasPermissionChangeProduct && api?.getCurrentSystem() === 'sws' && (
                      <div className="w-full p-4 rounded-lg border-2 border-secondary-100 bg-secondary-50">
                        <div className="flex items-start">
                          <CubeIcon className="h-5 w-5 text-secondary-400 mt-0.5 mr-3" />
                          <div className="flex-1">
                            <p className="font-medium text-secondary-600">Change Product (Not Available in Testing)</p>
                            <p className="text-sm text-secondary-500 mt-1">
                              Product changes are only available in the production environment due to a known issue with the testing server.
                            </p>
                            <p className="text-xs text-secondary-400 mt-1">
                              This feature works correctly when connected to the production SWS API.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Clear Tag Data - Only show if user has permission */}
                    {hasPermissionClearTag && (
                      <button
                        onClick={() => handleActionSelect('clear_tag')}
                        disabled={!canClearTag}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                          canClearTag
                            ? 'border-secondary-200 hover:border-warning-300 hover:bg-warning-50'
                            : 'border-secondary-100 bg-secondary-50 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-start">
                          <TrashIcon className="h-5 w-5 text-warning-600 mt-0.5 mr-3" />
                          <div className="flex-1">
                            <p className="font-medium text-secondary-900">Clear Tag Data</p>
                            <p className="text-sm text-secondary-600 mt-1">
                              Remove product association. Tag will become available for reuse.
                            </p>
                            {!canClearTag && tagMatch.status !== 'pending' && (
                              <p className="text-xs text-warning-600 mt-1">
                                Only available for pending tags
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Replace Tag - Only show if user has permission */}
                    {hasPermissionReplaceTag && (
                      <button
                        onClick={() => handleActionSelect('replace_tag')}
                        disabled={!canReplaceTag}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                          canReplaceTag
                            ? 'border-secondary-200 hover:border-error-300 hover:bg-error-50'
                            : 'border-secondary-100 bg-secondary-50 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-start">
                          <ArrowPathIcon className="h-5 w-5 text-error-600 mt-0.5 mr-3" />
                          <div className="flex-1">
                            <p className="font-medium text-secondary-900">Replace RFID Tag</p>
                            <p className="text-sm text-secondary-600 mt-1">
                              Tag is damaged or placed incorrectly and requires replacement. Original tag must be discarded.
                            </p>
                            {!canReplaceTag && tagMatch.status !== 'pending' && tagMatch.status !== 'on_hand' && (
                              <p className="text-xs text-warning-600 mt-1">
                                Only available for pending or on-hand tags
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                    </>
                  ) : (
                    /* Show message when user has no permissions */
                    <div className="text-center py-8">
                      <p className="text-secondary-600 mb-2">
                        You don't have permission to manage this tag.
                      </p>
                      <p className="text-sm text-secondary-500">
                        Contact your administrator if you need access.
                      </p>
                    </div>
                  )}

                  {/* Cancel Button */}
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Update Quantity Form */}
              {selectedAction === 'update_quantity' && (
                <form onSubmit={handleQuantityUpdate}>
                  <div className="mb-4">
                    <label htmlFor="quantity" className="block text-sm font-medium text-secondary-700 mb-2">
                      New Package Quantity
                    </label>
                    <div className="flex items-center justify-center space-x-4">
                      <button
                        type="button"
                        onClick={decrementQuantity}
                        disabled={parseInt(quantityInput) <= 1}
                        className="p-2 rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      
                      <input
                        id="quantity"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={quantityInput}
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        className={`w-20 text-center px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          quantityError
                            ? 'border-error-300 focus:ring-error-500 bg-error-50'
                            : 'border-secondary-300 focus:ring-primary-500'
                        }`}
                        placeholder="1"
                        autoFocus
                      />
                      
                      <button
                        type="button"
                        onClick={incrementQuantity}
                        className="p-2 rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                    {quantityError && (
                      <p className="mt-1 text-sm text-error-600">
                        {quantityError}
                      </p>
                    )}
                    <p className="mt-2 text-sm text-secondary-600">
                      Current quantity: {tagMatch.quantity}
                    </p>
                  </div>

                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => setSelectedAction(null)}
                      className="px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={!quantityInput || !!quantityError || parseInt(quantityInput) === tagMatch.quantity || isLoading}
                      className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating...
                        </>
                      ) : 'Update Quantity'}
                    </button>
                  </div>
                </form>
              )}

              {/* Change Product Form */}
              {selectedAction === 'change_product' && (
                <form onSubmit={handleProductChange}>
                  <div className="mb-4">
                    <label htmlFor="product-search" className="block text-sm font-medium text-secondary-700 mb-2">
                      Search for New Product
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="product-search"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchProducts())}
                        placeholder="Enter item number or description..."
                        className="flex-1 px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={searchProducts}
                        disabled={!searchTerm.trim()}
                        className={`px-4 py-2 font-medium text-sm rounded-md transition-colors ${
                          !searchTerm.trim()
                            ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                      >
                        Search
                      </button>
                    </div>
                    {!hasSearched && (
                      <p className="mt-2 text-sm text-secondary-500">
                        Enter a search term and click Search to find products
                      </p>
                    )}
                  </div>

                  {hasSearched && (
                    <div className="mb-4">
                      <div className="border border-secondary-200 rounded-md max-h-60 overflow-y-auto">
                        {loadingProducts ? (
                          <div className="p-4 text-center">
                            <LoadingSpinner size="sm" />
                          </div>
                        ) : products.length === 0 ? (
                          <div className="p-4 text-center text-secondary-500">
                            No products found for "{searchTerm}"
                          </div>
                        ) : (
                          <div>
                            <p className="px-3 py-2 text-sm text-secondary-600 border-b border-secondary-100">
                              Found {products.length} product{products.length !== 1 ? 's' : ''}
                            </p>
                            {products.map((product) => {
                              // Check if this is the product currently associated with the tag
                              const isCurrentProduct = product.itemNumber === currentProduct?.itemNumber;
                              
                              return (
                                <button
                                  key={product.id}
                                  type="button"
                                  onClick={() => product.status === 'active' && !isCurrentProduct && setSelectedProductId(product.id)}
                                  disabled={product.status === 'inactive' || isCurrentProduct}
                                  className={`w-full text-left p-3 border-b border-secondary-100 last:border-b-0 ${
                                    product.status === 'inactive' || isCurrentProduct
                                      ? 'bg-gray-50 cursor-not-allowed opacity-75' 
                                      : selectedProductId === product.id 
                                        ? 'bg-primary-50 border-primary-200 hover:bg-primary-100' 
                                        : 'hover:bg-secondary-50'
                                  }`}
                                >
                                  <div className="flex items-center">
                                    <input
                                      type="radio"
                                      checked={selectedProductId === product.id}
                                      onChange={() => product.status === 'active' && !isCurrentProduct && setSelectedProductId(product.id)}
                                      disabled={product.status === 'inactive' || isCurrentProduct}
                                      className="mr-3"
                                    />
                                    <div className="flex-1">
                                      <div className="flex items-center">
                                        <p className={`text-sm font-medium ${
                                          product.status === 'inactive' || isCurrentProduct ? 'text-gray-500' : 'text-secondary-900'
                                        }`}>
                                          {product.itemNumber}
                                        </p>
                                        <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                          product.status === 'active' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                          {product.status === 'active' ? 'Active' : 'Inactive'}
                                        </span>
                                        {isCurrentProduct && (
                                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                            Current
                                          </span>
                                        )}
                                      </div>
                                      <p className={`text-xs ${
                                        product.status === 'inactive' || isCurrentProduct ? 'text-gray-500' : 'text-secondary-600'
                                      }`}>
                                        {product.description}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              );
                            })
                          }
                          </div>
                        )}
                      </div>
                    </div>
                  )}


                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => setSelectedAction(null)}
                      className="px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={!selectedProductId || isLoading}
                      className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating...
                        </>
                      ) : 'Change Product'}
                    </button>
                  </div>
                </form>
              )}

              {/* Replace Tag Form */}
              {selectedAction === 'replace_tag' && !showReplaceConfirm && (
                <form onSubmit={handleReplaceTag}>
                  <div className="mb-4">
                    <label htmlFor="new-tag" className="block text-sm font-medium text-secondary-700 mb-2">
                      New Tag Number
                    </label>
                    <div className="flex gap-2">
                      <QRScannerButton 
                        onClick={() => setShowQRScanner(true)}
                        size="md"
                      />
                      <div className="relative flex-1">
                        <input
                          id="new-tag"
                          type="text"
                          value={newTagNumber}
                          onChange={(e) => handleNewTagNumberChange(e.target.value)}
                          placeholder="Enter new RFID tag number..."
                          className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono uppercase ${
                            tagCheckResult && !tagCheckResult.available
                              ? 'border-error-300 bg-error-50'
                              : tagCheckResult?.available
                              ? 'border-success-300 bg-success-50'
                              : 'border-secondary-300'
                          }`}
                          maxLength={12}
                          autoFocus
                        />
                        {isCheckingTag && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                          </div>
                        )}
                        {!isCheckingTag && tagCheckResult && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            {tagCheckResult.available ? (
                              <CheckCircleIcon className="h-5 w-5 text-success-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-error-500" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Tag validation feedback - Only show errors for duplicate tags */}
                    {tagCheckResult && !tagCheckResult.available && (
                      <div className="mt-2 p-3 bg-error-50 border border-error-200 rounded-md">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-error-400 mr-2 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-error-700 font-medium">
                              This tag is already in use
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    
                    {!tagCheckResult && newTagNumber.length < 12 && (
                      <p className="mt-2 text-sm text-secondary-600">
                        Enter or scan the RFID tag that will replace {tagMatch.tagNumber}
                      </p>
                    )}
                  </div>

                  <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 mb-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-warning-400 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-warning-700">
                          The original tag ({tagMatch.tagNumber}) will be unusable and must be discarded.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => setSelectedAction(null)}
                      className="px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={newTagNumber.length < 12 || !tagCheckResult?.available || isLoading}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      Continue
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quantity Update Confirmation Modal */}
      {showQuantityConfirm && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <HashtagIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-secondary-900">
                    Update Package Quantity
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-secondary-500">
                      Please confirm the quantity change:
                    </p>
                    <div className="mt-3 bg-secondary-50 rounded p-3">
                      <p className="text-sm">
                        <span className="font-medium">Tag:</span> <span className="font-mono">{tagMatch?.tagNumber}</span>
                      </p>
                      <p className="text-sm mt-1">
                        <span className="font-medium">Current quantity:</span> {tagMatch?.quantity}
                      </p>
                      <p className="text-sm mt-1">
                        <span className="font-medium">New quantity:</span> {quantityInput}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmQuantityUpdate}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isLoading ? 'Updating...' : 'Update Quantity'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowQuantityConfirm(false)}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Change Confirmation Modal */}
      {showProductConfirm && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <CubeIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-secondary-900">
                    Change Product Association
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-secondary-500">
                      Please confirm the product change:
                    </p>
                    <div className="mt-3 bg-secondary-50 rounded p-3">
                      <p className="text-sm">
                        <span className="font-medium">Tag:</span> <span className="font-mono">{tagMatch?.tagNumber}</span>
                      </p>
                      <p className="text-sm mt-1">
                        <span className="font-medium">Current product:</span> {currentProduct?.itemNumber || 'Unknown'}
                      </p>
                      <p className="text-sm mt-1">
                        <span className="font-medium">New product:</span> {(() => {
                          const selectedProduct = products.find(p => p.id === selectedProductId);
                          return selectedProduct ? selectedProduct.itemNumber : selectedProductId;
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmProductChange}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isLoading ? 'Updating...' : 'Change Product'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowProductConfirm(false)}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear Tag Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-warning-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-warning-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-secondary-900">
                    Clear Tag Data
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-secondary-500">
                      Are you sure you want to clear this tag's data? 
                    </p>
                    <p className="text-sm text-secondary-500 mt-2">
                      Tag <span className="font-mono font-medium">{tagMatch.tagNumber}</span> will:
                    </p>
                    <ul className="text-sm text-secondary-500 mt-1 ml-4 list-disc">
                      <li>Be unmatched from product {currentProduct?.itemNumber || 'Unknown'}</li>
                      <li>Change status to "Not Matched"</li>
                      <li>Become available for reuse with another product</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleClearTag}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-warning-600 text-base font-medium text-white hover:bg-warning-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-warning-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isLoading ? 'Clearing...' : 'Clear Tag Data'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowClearConfirm(false);
                    setSelectedAction(null);
                  }}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Replace Tag Confirmation Modal */}
      {showReplaceConfirm && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-error-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-error-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-secondary-900">
                    Replace RFID Tag
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-secondary-500">
                      Please confirm the tag replacement:
                    </p>
                    <div className="mt-3 bg-secondary-50 rounded p-3">
                      <p className="text-sm">
                        <span className="font-medium">Old tag:</span> <span className="font-mono">{tagMatch.tagNumber}</span>
                      </p>
                      <p className="text-sm mt-1">
                        <span className="font-medium">New tag:</span> <span className="font-mono">{newTagNumber}</span>
                      </p>
                    </div>
                    <div className="mt-3 bg-error-50 border border-error-200 rounded p-3">
                      <p className="text-sm text-error-700 font-medium">
                        ⚠️ The old tag cannot be reused and must be disposed of.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmReplaceTag}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-error-600 text-base font-medium text-white hover:bg-error-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-error-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isLoading ? 'Replacing...' : 'Replace Tag'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReplaceConfirm(false)}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* QR Scanner Modal for Replace Tag */}
      <SingleTagQRScannerModal
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onTagConfirmed={handleQRScanConfirmed}
        title="Scan Replacement Tag"
      />
    </div>
  );
};

export default EditTagModal;